import { motion } from 'framer-motion';
import { FC, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { StringKey } from '../../../../../lang/index';
import { EventExercisedTypeBackend, EventTypeBackend } from '../../../../../types/events.types';
import {
  EventTransactionsTableEmptyState,
  EventTransactionsTableLoadingState,
} from '../../../components/events-loading-placeholders';
import {
  EVENT_TYPES_WITH_EVENT_LEVEL_SIGNATURE_ONLY,
  tableHeadVariant,
} from './event-table-types.types';
import { SIGNATURE_STATUS_COLUMN_CLASS } from './table-td';

export type BasicTableProps = {
  children: ReactNode;
  className?: string;
  eventType?: EventExercisedTypeBackend | EventTypeBackend;
  headers?: StringKey[];
  isEmpty?: boolean;
  isFilterResult?: boolean;
  isLoading?: boolean;
  loadingRows?: number;
  transactionView?: boolean;
};

const shouldRenderTableColumn = ({
  eventType,
  fieldName,
  transactionView,
}: {
  eventType: EventExercisedTypeBackend | EventTypeBackend;
  fieldName: string;
  transactionView?: boolean;
}) => {
  if (transactionView && fieldName === 'FILE') return false;

  if (
    !transactionView &&
    fieldName === 'SIGNATURE_STATUS' &&
    EVENT_TYPES_WITH_EVENT_LEVEL_SIGNATURE_ONLY.includes(eventType as EventTypeBackend)
  ) {
    return false;
  }

  return true;
};

export const getVisibleTableColumnCount = (
  eventType: EventExercisedTypeBackend | EventTypeBackend,
  transactionView?: boolean,
) =>
  tableHeadVariant[eventType].filter((fieldName) =>
    shouldRenderTableColumn({ eventType, fieldName, transactionView }),
  ).length;

export const BasicTable: FC<BasicTableProps> = ({
  eventType,
  headers,
  children,
  className,
  transactionView,
  isEmpty = false,
  isFilterResult = false,
  isLoading = false,
  loadingRows = 1,
}) => {
  const headItems = headers ?? (eventType ? tableHeadVariant[eventType] : []);
  const visibleColumns = headItems.filter(
    (fieldName) => !eventType || shouldRenderTableColumn({ eventType, fieldName, transactionView }),
  );
  const columnCount = visibleColumns.length;

  return (
    <table className={twMerge('w-full shrink-0 overflow-hidden text-nowrap rounded-md', className)}>
      <thead className="h-11 rounded-t-md border-b border-[#F2F2F2] bg-[#F9FAFB]">
        <tr>
          {headItems.map((fieldName) =>
            !eventType || shouldRenderTableColumn({ eventType, fieldName, transactionView }) ? (
              <th
                className={twMerge(
                  'pl-4 text-left',
                  fieldName === 'SIGNATURE_STATUS' && SIGNATURE_STATUS_COLUMN_CLASS,
                  // The row actions column: room for one 24px menu button.
                  fieldName === 'EMPTY' && 'w-12',
                )}
                key={fieldName}
              >
                <span className="text-label-md font-medium text-[#172335]">
                  <AppFormattedMessage id={fieldName} />
                </span>
              </th>
            ) : null,
          )}
        </tr>
      </thead>
      <motion.tbody
        animate={{ opacity: 1 }}
        className="divide-y divide-gray-100 border-b border-[#F2F2F2]"
        initial={{ opacity: 0 }}
        key={isLoading ? 'loading' : isEmpty ? 'empty' : 'data'}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {isLoading ? (
          <EventTransactionsTableLoadingState rows={loadingRows} visibleColumns={visibleColumns} />
        ) : isEmpty ? (
          <EventTransactionsTableEmptyState colSpan={columnCount} isFilterResult={isFilterResult} />
        ) : (
          children
        )}
      </motion.tbody>
    </table>
  );
};
